// ./src/components/features/Cars/CarSelector/CarSelector.tsx
import React, { useMemo, useCallback, useState } from "react";
import {
  Select,
  SelectProps,
  Group,
  Text,
  Stack,
  Box,
  ComboboxProps,
  TextInput,
  CloseButton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Car } from "@/utils/api";
import { useDashboardContext, useAppEnvironment } from "@/contexts";
import { useCars } from "@/hooks";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import { getCarDisplayName } from "@/utils/carHelpers";
import { CarPickerModal } from "./CarPickerModal";

// ============================================
// Types
// ============================================

export interface CarSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected car ID */
  value?: string | null | undefined;

  /** Callback fired when car selection changes: (name, value, car?) */
  onChange?: (name: string, value: string | null, car?: Car) => void;

  /** Placeholder text when no car is selected */
  placeholder?: string;

  /**
   * Whether to include inactive cars in the list.
   * When false (default), uses active cars from DashboardContext (no extra fetch).
   * When true, loads all cars via a separate useCars hook.
   */
  includeInactive?: boolean;

  /** Custom filter function to filter cars */
  carFilter?: (car: Car) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no cars are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show car details (make/model) in dropdown */
  showDetails?: boolean;

  /** Label for the field */
  label?: string;

  /** Whether to show "All Vehicles" option */
  showAllOption?: boolean;

  /** Label for "All Vehicles" option */
  allOptionLabel?: string;

  /** Props passed to the underlying Combobox (e.g., withinPortal) */
  comboboxProps?: ComboboxProps;

  withinPortal?: boolean;
}

// ============================================
// Option Data Interface
// ============================================

interface CarOptionData {
  value: string;
  label: string;
  details?: string;
  disabled?: boolean;
}

// ============================================
// Constants
// ============================================

const ALL_VEHICLES_VALUE = "__all__";

// ============================================
// Component
// ============================================

const CarSelectorComponent: React.FC<CarSelectorProps> = ({
  name = "carId",
  value,
  onChange,
  placeholder,
  includeInactive = false,
  carFilter,
  required = false,
  loadingPlaceholder,
  emptyText,
  error,
  disabled,
  size = "md",
  showDetails = true,
  label,
  showAllOption = false,
  allOptionLabel,
  comboboxProps,
  withinPortal,
  ...selectProps
}) => {
  const { t } = useTranslation(["refuels", "common"]);
  const { isMobile } = useAppEnvironment();

  // Active cars from dashboard context (already loaded, no extra fetch)
  const {
    cars: activeCars,
    loading: dashboardLoading,
    getCarById: getDashboardCarById,
  } = useDashboardContext();

  // All-status cars hook — only fetches when includeInactive is true
  const {
    cars: allCars,
    loading: allCarsLoading,
    getCarById: getAllCarById,
    lastError: allCarsError,
  } = useCars({
    autoLoad: includeInactive,
    initialFilters: includeInactive ? {} : { status: [] },
  });

  // Resolve which car list and loading state to use
  const availableCars = includeInactive ? allCars : activeCars;
  const loading = includeInactive ? allCarsLoading : dashboardLoading;
  const lastError = includeInactive ? allCarsError : null;

  // Modal state (mobile)
  const [modalOpened, setModalOpened] = useState(false);

  // Unified car lookup (check both sources)
  const getCarById = useCallback(
    (id: string | undefined): Car | undefined => {
      if (!id) return undefined;
      return getDashboardCarById(id) ?? getAllCarById(id);
    },
    [getDashboardCarById, getAllCarById]
  );

  // Default translations
  const defaultPlaceholder =
    placeholder || t("refuels:selectors.car.placeholder");
  const defaultLabel = label;
  const defaultLoadingPlaceholder =
    loadingPlaceholder || t("refuels:selectors.car.loading");
  const defaultEmptyText = emptyText || t("refuels:selectors.car.empty");
  const defaultAllOptionLabel = allOptionLabel || t("refuels:list.allVehicles");

  // Create a map for quick car lookup by ID
  const carMap = useMemo(() => {
    const map = new Map<string, Car>();
    availableCars.forEach((car) => {
      if (car.id) {
        map.set(car.id, car);
      }
    });
    return map;
  }, [availableCars]);

  // Apply custom filter
  const filteredCars = useMemo(() => {
    if (!carFilter) return availableCars;
    return availableCars.filter(carFilter);
  }, [availableCars, carFilter]);

  // Filter and prepare car options (desktop)
  const carOptions = useMemo((): CarOptionData[] => {
    const options: CarOptionData[] = [];

    if (showAllOption) {
      options.push({
        value: ALL_VEHICLES_VALUE,
        label: defaultAllOptionLabel,
        details: undefined,
      });
    }

    filteredCars.forEach((car) => {
      if (car.id) {
        options.push({
          value: car.id,
          label: getCarDisplayName(car) || car.label || "Unnamed Vehicle",
          details: showDetails
            ? [car.vehicleMake?.makeName, car.model]
              .filter(Boolean)
              .join(" ") || undefined
            : undefined,
        });
      }
    });

    return options;
  }, [filteredCars, showAllOption, defaultAllOptionLabel, showDetails]);

  // Handle onChange with car object lookup
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      if (selectedValue === ALL_VEHICLES_VALUE) {
        onChange?.(name, null, undefined);
      } else if (selectedValue) {
        const car = carMap.get(selectedValue) || getCarById(selectedValue);
        onChange?.(name, selectedValue, car);
      } else {
        onChange?.(name, null, undefined);
      }
    },
    [name, carMap, getCarById, onChange]
  );

  // Handle modal selection (mobile)
  const handleModalSelect = useCallback(
    (carId: string | null, car?: Car) => {
      onChange?.(name, carId, car);
    },
    [name, onChange]
  );

  // Handle clear (mobile)
  const handleClear = useCallback(() => {
    onChange?.(name, null, undefined);
  }, [name, onChange]);

  // Get display text for the selected car
  const selectedDisplayText = useMemo(() => {
    if (value === null && showAllOption) return defaultAllOptionLabel;
    if (!value) return "";
    const car = carMap.get(value) || getCarById(value);
    if (!car) return "";
    return getCarDisplayName(car) || car.label || "Unnamed Vehicle";
  }, [value, showAllOption, defaultAllOptionLabel, carMap, getCarById]);

  // Render option for dropdown (desktop)
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const optionData = option as CarOptionData;

      if (optionData.value === ALL_VEHICLES_VALUE) {
        return (
          <Group gap="sm" wrap="nowrap">
            <AppIcon iconName="Car" size={24} />
            <Text size="md" fw={500}>
              {optionData.label}
            </Text>
          </Group>
        );
      }

      return (
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <Box pt={2}>
            <AppIcon iconName="Car" size={24} />
          </Box>
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={500} lineClamp={1}>
              {optionData.label}
            </Text>
            {optionData.details && (
              <Text size="sm" c="dimmed" lineClamp={1}>
                {optionData.details}
              </Text>
            )}
          </Stack>
        </Group>
      );
    },
    []
  );

  // Memoize left section icon
  const leftSection = useMemo(() => {
    return <AppIcon iconName="Car" size={24} />;
  }, []);

  // Determine current state
  const isEmpty = !loading && availableCars.length === 0;

  // ==========================================
  // Mobile: Read-only input + full-screen modal
  // ==========================================
  if (isMobile) {
    const isDisabled =
      disabled || (loading && availableCars.length === 0) || (isEmpty && !showAllOption);

    const mobilePlaceholder = loading && availableCars.length === 0
      ? defaultLoadingPlaceholder
      : isEmpty && !showAllOption
        ? defaultEmptyText
        : defaultPlaceholder;

    const mobileRightSection = (() => {
      if (loading && availableCars.length === 0) {
        return <LoadingIndicator size={size} color="brand" />;
      }
      if (selectedDisplayText && !required && !disabled) {
        return (
          <CloseButton
            size={size}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            aria-label={t("common:form.clearSelection")}
          />
        );
      }
      return <AppIcon iconName="ChevronDown" size={18} />;
    })();

    return (
      <Box>
        <TextInput
          label={defaultLabel}
          placeholder={mobilePlaceholder}
          value={selectedDisplayText}
          readOnly
          onClick={() => {
            if (!isDisabled) {
              setModalOpened(true);
            }
          }}
          leftSection={leftSection}
          rightSection={mobileRightSection}
          rightSectionPointerEvents={
            selectedDisplayText && !required && !disabled ? "auto" : "none"
          }
          error={
            lastError && !loading && availableCars.length === 0
              ? error || t("common:messages.loadError")
              : error
          }
          required={required}
          disabled={isDisabled}
          size={size}
          styles={{
            input: {
              cursor: isDisabled ? "not-allowed" : "pointer",
              caretColor: "transparent",
            },
          }}
        />

        <CarPickerModal
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          value={value}
          onSelect={handleModalSelect}
          cars={filteredCars}
          showAllOption={showAllOption}
          allOptionLabel={defaultAllOptionLabel}
          showDetails={showDetails}
        />
      </Box>
    );
  }

  // ==========================================
  // Desktop: Original Select behavior
  // ==========================================

  // Memoize select state
  const selectState = useMemo(() => {
    if (loading && availableCars.length === 0) {
      return {
        data: [] as CarOptionData[],
        placeholder: defaultLoadingPlaceholder,
        isDisabled: true,
        showLoading: true,
        errorMessage: undefined as string | undefined,
      };
    }

    if (lastError && !loading && availableCars.length === 0) {
      return {
        data: [] as CarOptionData[],
        placeholder: t("common:errors.generic"),
        isDisabled: true,
        showLoading: false,
        errorMessage: error || t("common:messages.loadError"),
      };
    }

    if (isEmpty && !showAllOption) {
      return {
        data: [] as CarOptionData[],
        placeholder: defaultEmptyText,
        isDisabled: true,
        showLoading: false,
        errorMessage: undefined as string | undefined,
      };
    }

    return {
      data: carOptions,
      placeholder: defaultPlaceholder,
      isDisabled: disabled || loading,
      showLoading: loading && availableCars.length > 0,
      errorMessage: undefined as string | undefined,
    };
  }, [
    loading,
    availableCars.length,
    lastError,
    isEmpty,
    showAllOption,
    carOptions,
    defaultLoadingPlaceholder,
    defaultEmptyText,
    defaultPlaceholder,
    disabled,
    error,
    t,
  ]);

  // Memoize right section
  const rightSection = useMemo(() => {
    if (selectState.showLoading) {
      return <LoadingIndicator size={size} color="brand" />;
    }
    return selectProps.rightSection;
  }, [selectState.showLoading, size, selectProps.rightSection]);

  // Determine display value (handle "All Vehicles" case)
  const displayValue = useMemo(() => {
    if (value === null && showAllOption) {
      return ALL_VEHICLES_VALUE;
    }
    return value ?? null;
  }, [value, showAllOption]);

  // Combobox props for portal rendering
  const comboboxPropsPortal = useMemo(() => {
    if (!withinPortal) return undefined;
    return {
      withinPortal: true,
      zIndex: 1000,
    };
  }, [withinPortal]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={displayValue}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required && !showAllOption}
        required={required}
        allowDeselect={!required}
        disabled={selectState.isDisabled}
        error={selectState.errorMessage || error}
        data={selectState.data}
        rightSection={rightSection}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions")}
        comboboxProps={{
          ...(comboboxProps || {}),
          ...(comboboxPropsPortal || {}),
        }}
      />
    </Box>
  );
};

export const CarSelector = React.memo(CarSelectorComponent);